import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { hrApi } from '@/api';
import { Button } from '@/components/ui/Button';
import { CurrencySelect } from '@/components/selects';
import { Skeleton } from '@/components/ui/Skeleton';
import { HrLeaveReasonAttachmentFields } from '@/modules/hr/components/HrLeaveReasonAttachmentFields';
import { uploadHrDocument } from '@/modules/hr/components/leaveAttachmentUtils';
import { tierOptions } from '@/lib/query';
import type { HrTravelRequest } from '@/types/hr';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

interface Props {
  travelId: number;
  onSaved?: () => void;
  onCancel?: () => void;
}

export function ReturnedTravelEditPanel({ travelId, onSaved, onCancel }: Props) {
  const { t } = useTranslation('hr');
  const [origin, setOrigin] = useState('');
  const [destination, setDestination] = useState('');
  const [purpose, setPurpose] = useState('');
  const [departureDate, setDepartureDate] = useState('');
  const [returnDate, setReturnDate] = useState('');
  const [estimatedCost, setEstimatedCost] = useState('');
  const [advance, setAdvance] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [existingAttachmentUrl, setExistingAttachmentUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const { data: travelRows = [], isLoading } = useQuery({
    queryKey: ['hr', 'travel-request', travelId],
    queryFn: () => hrApi.listTravel(),
    ...tierOptions('operational'),
  });

  const travel = useMemo(
    () => travelRows.find((row: HrTravelRequest) => row.id === travelId),
    [travelRows, travelId],
  );

  useEffect(() => {
    if (!travel) return;
    setOrigin(travel.originLocation ?? '');
    setDestination(travel.destinationLocation ?? '');
    setPurpose(travel.purpose ?? '');
    setDepartureDate(travel.departureDate ?? '');
    setReturnDate(travel.returnDate ?? '');
    setEstimatedCost(travel.estimatedCost != null ? String(travel.estimatedCost) : '');
    setAdvance(travel.advanceAmount != null ? String(travel.advanceAmount) : '');
    setCurrency(travel.currencyCode ?? 'USD');
    setExistingAttachmentUrl(travel.attachmentUrl ?? null);
    setAttachmentFile(null);
  }, [travel]);

  const save = async () => {
    if (!travel || !destination.trim() || !purpose.trim() || !departureDate) {
      notify.error(t('errors.submitFailed'));
      return;
    }
    setSaving(true);
    setUploading(Boolean(attachmentFile));
    try {
      const uploadedUrl = attachmentFile ? await uploadHrDocument(attachmentFile) : undefined;
      const resolvedAttachmentUrl =
        uploadedUrl !== undefined
          ? uploadedUrl
          : existingAttachmentUrl === null
            ? ''
            : existingAttachmentUrl ?? undefined;
      await hrApi.updateTravel(travelId, {
        purpose: purpose.trim(),
        origin: origin.trim() || undefined,
        destination: destination.trim(),
        departureDate,
        returnDate: returnDate || undefined,
        estimatedCost: estimatedCost ? Number(estimatedCost) : undefined,
        advance: advance ? Number(advance) : undefined,
        currency,
        attachmentUrl: resolvedAttachmentUrl,
      });
      notify.t.success('toast.leaveUpdatedResubmitWhenReady', { ns: 'workflow' });
      onSaved?.();
    } catch (err) {
      showApiError(err, t('errors.submitFailed'));
    } finally {
      setSaving(false);
      setUploading(false);
    }
  };

  if (isLoading) {
    return <Skeleton className="h-32 w-full" />;
  }

  if (!travel) {
    return <p className="text-sm text-slate-500">Travel request not found.</p>;
  }

  return (
    <div className="space-y-3 rounded-lg border border-amber-200 bg-amber-50/50 p-3 dark:border-amber-900 dark:bg-amber-950/20">
      <p className="text-sm font-medium text-slate-700 dark:text-slate-200">Edit travel request</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block text-sm">
          <span className="mb-1 block font-medium">Origin</span>
          <input className="input w-full" value={origin} onChange={(e) => setOrigin(e.target.value)} />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block font-medium">Destination</span>
          <input
            className="input w-full"
            value={destination}
            onChange={(e) => setDestination(e.target.value)}
            required
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block font-medium">Departure</span>
          <input
            type="date"
            className="input w-full"
            value={departureDate}
            onChange={(e) => setDepartureDate(e.target.value)}
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block font-medium">Return</span>
          <input
            type="date"
            className="input w-full"
            value={returnDate}
            onChange={(e) => setReturnDate(e.target.value)}
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block font-medium">Estimated cost</span>
          <input
            type="number"
            min="0"
            step="0.01"
            className="input w-full"
            value={estimatedCost}
            onChange={(e) => setEstimatedCost(e.target.value)}
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block font-medium">Advance</span>
          <input
            type="number"
            min="0"
            step="0.01"
            className="input w-full"
            value={advance}
            onChange={(e) => setAdvance(e.target.value)}
          />
        </label>
        <CurrencySelect value={currency} onChange={setCurrency} required />
      </div>
      <HrLeaveReasonAttachmentFields
        reason={purpose}
        onReasonChange={setPurpose}
        attachmentFile={attachmentFile}
        onAttachmentFileChange={setAttachmentFile}
        existingAttachmentUrl={existingAttachmentUrl}
        onClearExistingAttachment={() => setExistingAttachmentUrl(null)}
        uploading={uploading}
        reasonLabel="Purpose"
        attachmentLabel="Supporting document (optional)"
        attachmentHint="Upload travel itinerary, invitation letter, or other supporting file."
      />
      <div className="flex flex-wrap gap-2">
        <Button size="sm" onClick={() => void save()} disabled={saving}>
          Save changes
        </Button>
        {onCancel ? (
          <Button size="sm" variant="secondary" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
        ) : null}
      </div>
    </div>
  );
}
